<template>
  <div>
    <!-- SECTION EXPOSITION -->
    <div v-if="!homePage" class="exhibition-wrapper">
      <!-- Header avec effet parallaxe -->
      <section class="hero-section" :style="heroBackgroundStyle">
        <div class="hero-content">
          <h1 class="hero-title">{{ name }}</h1>
          <div class="hero-subtitle">Exposition Virtuelle</div>
        </div>
        <div class="scroll-indicator">
          <span class="scroll-text">Découvrir</span>
          <div class="scroll-line"></div>
        </div>
      </section>

      <!-- Menu de navigation flottant -->
      <div class="floating-menu">
        <button 
          class="menu-item" 
          :class="{ active: viewMode === 'magazine' }"
          @click="viewMode = 'magazine'"
        >
          Magazine
        </button>
        <button 
          class="menu-item" 
          :class="{ active: viewMode === 'grid' }"
          @click="viewMode = 'grid'"
        >
          Grille
        </button>
        <div class="search-wrapper">
          <input 
            type="text" 
            v-model="searchQuery" 
            placeholder="Rechercher..." 
            class="search-input"
          />
        </div>
      </div>

      <!-- Vue Magazine -->
      <section v-if="viewMode === 'magazine'" class="magazine-layout">
        <div class="magazine-container">
          <!-- Featured Item (Premier élément en grand) -->
          <div 
            v-if="filteredResources.length > 0"
            class="featured-item"
            @click="openLightbox(getOriginalIndex(0))"
          >
            <div class="featured-image">
              <img 
                :src="resourceUrls[getOriginalIndex(0)]" 
                :alt="filteredResources[0].title"
              />
              <div class="featured-overlay">
                <h2>{{ filteredResources[0].title }}</h2>
                <p>{{ filteredResources[0].description }}</p>
                <span class="resource-type">{{ getResourceType(filteredResources[0]) }}</span>
              </div>
            </div>
          </div>

          <!-- Magazine Grid pour les autres éléments -->
          <div class="magazine-grid">
            <div 
              v-for="(resource, index) in filteredResources.slice(1)"
              :key="index"
              class="magazine-item"
              :class="getItemClass(index)"
              @click="openLightbox(getOriginalIndex(index + 1))"
            >
              <div class="magazine-item-content">
                <div class="item-image">
                  <img 
                    :src="resourceUrls[getOriginalIndex(index + 1)]" 
                    :alt="resource.title"
                  />
                </div>
                <div class="item-info">
                  <h3>{{ resource.title }}</h3>
                  <span class="resource-type">{{ getResourceType(resource) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Vue Grille Alternative -->
      <section v-else class="grid-layout">
        <div class="masonry-grid">
          <div 
            v-for="(resource, index) in filteredResources"
            :key="index"
            class="masonry-item"
            :style="getMasonryStyle(index)"
            @click="openLightbox(getOriginalIndex(index))"
          >
            <div class="masonry-content">
              <img 
                :src="resourceUrls[getOriginalIndex(index)]" 
                :alt="resource.title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <h3>{{ resource.title }}</h3>
                <p class="resource-type">{{ getResourceType(resource) }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Message pas de résultats -->
      <div v-if="filteredResources.length === 0" class="no-results">
        <div class="no-results-content">
          <span class="icon">🔍</span>
          <h3>Aucun résultat trouvé</h3>
          <p>Essayez d'autres termes de recherche</p>
        </div>
      </div>

      <!-- Footer créatif -->
      <footer class="creative-footer">
        <div class="footer-pattern"></div>
        <div class="footer-content">
          <div class="footer-info">
            <span>2023 ALPA TECHNOLOGIES</span>
            <span>Mentions Légales</span>
            <span>Fattu in Corsica</span>
          </div>
        </div>
      </footer>
    </div>

    <!-- SECTION HOME -->
    <div v-else class="home-section">
      <component
        :is="dynamicComponent"
        :exhibitIdProps="exhibitId"
        :homeImage="homeImage"
        @exit="exitHomePage"
      ></component>
    </div>

    <!-- LIGHTBOX AMÉLIORÉE -->
    <div
      v-if="selectedImageIndex !== null"
      class="lightbox"
      @click="closeLightbox"
    >
      <div class="lightbox-container" @click.stop>
        <div class="lightbox-media">
          <img
            :src="resourceUrls[selectedImageIndex]"
            :alt="resources[selectedImageIndex]?.title"
            class="lightbox-image"
          />
        </div>
        
        <div class="lightbox-details">
          <div class="details-header">
            <h2>{{ resources[selectedImageIndex]?.title }}</h2>
            <span class="resource-type">{{ getResourceType(resources[selectedImageIndex]) }}</span>
          </div>
          
          <div class="details-content">
            <p>{{ resources[selectedImageIndex]?.description }}</p>
          </div>

          <div class="lightbox-nav">
            <button 
              class="nav-button prev" 
              @click.stop="navigateLightbox(-1)"
              :disabled="selectedImageIndex === 0"
            >
              <span class="nav-icon">←</span>
              <span class="nav-text">Précédent</span>
            </button>
            <button 
              class="nav-button next" 
              @click.stop="navigateLightbox(1)"
              :disabled="selectedImageIndex === resources.length - 1"
            >
              <span class="nav-text">Suivant</span>
              <span class="nav-icon">→</span>
            </button>
          </div>
        </div>

        <button class="lightbox-close" @click.stop="closeLightbox">
          <span>×</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, defineProps } from 'vue';
import { useRoute } from 'vue-router';
import axios from "axios";

// Import des modèles
import Modele_1 from "../../components/Models/Modele_1.vue";
import Modele_2 from "../../components/Models/Modele_2.vue";
import Modele_3 from "../../components/Models/Modele_3.vue";
import Modele_4 from "../../components/Models/Modele_4.vue";
import Modele_5 from "../../components/Models/Modele_5.vue";
import Modele_6 from "../../components/Models/Modele_6.vue";
import Modele_7 from "../../components/Models/Modele_7.vue";

const props = defineProps({
  homePageProps: {
    type: Boolean,
    default: true,
  },
  exhibitId: {
    type: String,
    default: null,
  },
});

// États
const route = useRoute();
const id = ref(props.exhibitId ?? route.params.id);
const resources = ref([]);
const name = ref("");
const userName = ref("");
const homeImage = ref({});
const resourceUrls = ref([]);
const homePage = ref(props.homePageProps);
const selectedTemplate = ref("");
const selectedImageIndex = ref(null);
const viewMode = ref('magazine');
const searchQuery = ref('');

// Styles dynamiques
const heroBackgroundStyle = computed(() => {
  if (resourceUrls.value.length > 0) {
    return {
      backgroundImage: `url(${resourceUrls.value[0]})`,
    };
  }
  return {};
});

// Composants dynamiques
const modeleComponents = {
  Modele_1, Modele_2, Modele_3, Modele_4, Modele_5, Modele_6, Modele_7
};

const dynamicComponent = computed(() => {
  return modeleComponents[selectedTemplate.value] || null;
});

// Filtrage des ressources
const filteredResources = computed(() => {
  if (!searchQuery.value) return resources.value;
  const query = searchQuery.value.toLowerCase();
  return resources.value.filter(resource => 
    resource.title?.toLowerCase().includes(query) || 
    resource.description?.toLowerCase().includes(query)
  );
});

// Fonctions utilitaires
function getOriginalIndex(filteredIndex) {
  const filteredResource = filteredResources.value[filteredIndex];
  return resources.value.findIndex(r => r.id === filteredResource.id);
}

function getResourceType(resource) {
  if (!resource) return 'Autre';
  const name = resource.name?.toLowerCase() || '';
  if (name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.avi')) {
    return 'Vidéo';
  } else if (name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.png')) {
    return 'Image';
  } else if (name.endsWith('.pdf')) {
    return 'Document';
  }
  return 'Autre';
}

function getItemClass(index) {
  const classes = ['wide', 'tall', 'normal'];
  return classes[index % classes.length];
}

function getMasonryStyle(index) {
  const heights = ['350px', '400px', '450px'];
  return {
    height: heights[index % heights.length]
  };
}

// Fonctions API
async function getExhibitName() {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/exhibit/${id.value}`;
    const response = await axios.get(url);
    name.value = response.data.exhibit_name;
  } catch (error) {
    console.error("Error getting exhibit name:", error);
  }
}

async function getUserName() {
  try {
    const url = `${process.env.VUE_APP_BASEURL}/user/${id.value}`;
    const response = await axios.get(url);
    userName.value = response.data.name;
  } catch (error) {
    console.error(error);
  }
}

async function getHomeImage() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const url = `${process.env.VUE_APP_BASEURL}/exhibit_home/${exhibitParam}`;
    const response = await axios.get(url);
    homeImage.value = response.data[0];
  } catch (error) {
    console.log(error);
  }
}

async function getModel() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const url = `${process.env.VUE_APP_BASEURL}/model/${exhibitParam}`;
    const response = await axios.get(url);
    if (response.data.length > 0) {
      let modelName = response.data[0].model;
      modelName = modelName.replace(/è/g, 'e').replace(/\s+/g, '_');
      selectedTemplate.value = modelName;
    }
  } catch (error) {
    console.log(error);
  }
}

async function getResources() {
  try {
    const exhibitParam = props.exhibitId ?? route.params.id;
    const resourcesUrl = `${process.env.VUE_APP_BASEURL}/resources`;
    const response = await axios.post(resourcesUrl, { exhibitId: exhibitParam });
    const allResources = response.data;

    const galleryUrl = `${process.env.VUE_APP_BASEURL}/galleryResources/${exhibitParam}`;
    const galleryResponse = await axios.get(galleryUrl);
    const galleryResourceIds = galleryResponse.data.map(res => res.resource_id);

    resources.value = allResources.filter(resource =>
      galleryResourceIds.includes(resource.id)
    );
  } catch (error) {
    console.error("Error fetching resources:", error);
  }
}

async function fetchResources() {
  if (resources.value.length === 0) {
    await getResources();
  }
  const urls = resources.value.map(
    (r) => `${process.env.VUE_APP_BASEURL}/s3Resource/${r.name}`
  );
  try {
    const responses = await Promise.all(urls.map(url => axios.get(url)));
    resourceUrls.value = responses.map(r => r.data);
  } catch (error) {
    console.error("Erreur lors de la récupération des URLs", error);
  }
}

// Gestion de la lightbox
function openLightbox(index) {
  selectedImageIndex.value = index;
}

function closeLightbox() {
  selectedImageIndex.value = null;
}

function navigateLightbox(direction) {
  const newIndex = selectedImageIndex.value + direction;
  if (newIndex >= 0 && newIndex < resources.value.length) {
    selectedImageIndex.value = newIndex;
  }
}

function exitHomePage() {
  homePage.value = false;
}

// Lifecycle
onMounted(async () => {
  await getHomeImage();
  await getResources();
  await fetchResources();
  await getExhibitName();
  await getUserName();
  await getModel();
});
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700&family=Poppins:wght@300;400;500;600&display=swap');

:root {
  --primary-color: #2c3e50;
  --accent-color: #e74c3c;
  --text-color: #2c3e50;
  --light-bg: #f8f9fa;
  --dark-bg: #2c3e50;
  --transition: all 0.3s ease;
}

/* Styles globaux */
.exhibition-wrapper {
  font-family: 'Poppins', sans-serif;
  color: var(--text-color);
  background-color: var(--light-bg);
}

/* Hero Section */
.hero-section {
  height: 100vh;
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0,0,0,0.3), rgba(0,0,0,0.7));
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-title {
  font-family: 'Playfair Display', serif;
  font-size: 5rem;
  margin-bottom: 1rem;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInUp 1s ease forwards;
}

.hero-subtitle {
  font-size: 1.5rem;
  letter-spacing: 4px;
  text-transform: uppercase;
  opacity: 0;
  transform: translateY(30px);
  animation: fadeInUp 1s ease forwards 0.3s;
}

.scroll-indicator {
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  animation: fadeIn 1s ease forwards 1s;
}

.scroll-text {
  font-size: 0.9rem;
  letter-spacing: 2px;
  margin-bottom: 10px;
}

.scroll-line {
  width: 2px;
  height: 60px;
  background: white;
  position: relative;
  overflow: hidden;
}

.scroll-line::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255,255,255,0.7);
  animation: scrollDown 2s ease infinite;
}

/* Menu flottant */
.floating-menu {
  position: fixed;
  top: 30px;
  right: 30px;
  z-index: 100;
  display: flex;
  gap: 15px;
  padding: 15px;
  background: rgba(255,255,255,0.95);
  border-radius: 50px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.1);
}

.menu-item {
  background: none;
  border: none;
  padding: 8px 20px;
  border-radius: 25px;
  font-family: 'Poppins', sans-serif;
  font-size: 0.9rem;
  cursor: pointer;
  transition: var(--transition);
}

.menu-item.active {
  background: var(--accent-color);
  color: white;
}

.search-wrapper {
  position: relative;
}

.search-input {
  background: rgba(0,0,0,0.05);
  border: none;
  padding: 8px 20px;
  border-radius: 25px;
  font-family: 'Poppins', sans-serif;
  width: 200px;
  transition: var(--transition);
}

.search-input:focus {
  outline: none;
  background: rgba(0,0,0,0.08);
  width: 250px;
}

/* Vue Magazine */
.magazine-layout {
  padding: 4rem 2rem;
}

.magazine-container {
  max-width: 1400px;
  margin: 0 auto;
}

.featured-item {
  margin-bottom: 4rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.1);
}

.featured-image {
  position: relative;
  height: 70vh;
}

.featured-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.featured-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 3rem;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
  transform: translateY(20px);
  opacity: 0;
  transition: var(--transition);
}

.featured-item:hover .featured-overlay {
  transform: translateY(0);
  opacity: 1;
}

.featured-overlay h2 {
  font-family: 'Playfair Display', serif;
  font-size: 2.5rem;
  margin-bottom: 1rem;
}

.magazine-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 2rem;
}

.magazine-item {
  position: relative;
  cursor: pointer;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: var(--transition);
}

.magazine-item.wide {
  grid-column: span 2;
}

.magazine-item.tall {
  grid-row: span 2;
}

.magazine-item:hover {
  transform: translateY(-10px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.2);
}

.magazine-item-content {
  position: relative;
  height: 100%;
}

.item-image {
  height: 100%;
  min-height: 300px;
}

.item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.item-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
  transform: translateY(100%);
  transition: var(--transition);
}

.magazine-item:hover .item-info {
  transform: translateY(0);
}

/* Vue Grille */
.masonry-grid {
  columns: 3 250px;
  column-gap: 2rem;
  padding: 4rem 2rem;
  max-width: 1400px;
  margin: 0 auto;
}

.masonry-item {
  break-inside: avoid;
  margin-bottom: 2rem;
  position: relative;
  cursor: pointer;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  transition: var(--transition);
}

.masonry-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.2);
}

.masonry-content {
  position: relative;
  width: 100%;
}

.masonry-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.masonry-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 1.5rem;
  background: linear-gradient(to top, rgba(0,0,0,0.8), transparent);
  color: white;
  transform: translateY(100%);
  transition: var(--transition);
}

.masonry-item:hover .masonry-overlay {
  transform: translateY(0);
}

/* Message pas de résultats */
.no-results {
  min-height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.no-results-content {
  padding: 2rem;
}

.no-results .icon {
  font-size: 3rem;
  margin-bottom: 1rem;
  display: block;
}

/* Footer créatif */
.creative-footer {
  position: relative;
  background: var(--dark-bg);
  color: white;
  padding: 4rem 2rem;
  margin-top: 4rem;
}

.footer-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  background: linear-gradient(45deg, var(--accent-color) 25%, transparent 25%),
              linear-gradient(-45deg, var(--accent-color) 25%, transparent 25%);
  background-size: 20px 20px;
  opacity: 0.1;
}

.footer-content {
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  text-align: center;
}

.footer-info {
  display: flex;
  justify-content: center;
  gap: 2rem;
  font-size: 0.9rem;
  letter-spacing: 1px;
}

/* Lightbox améliorée */
.lightbox {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.95);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  animation: fadeIn 0.3s ease forwards;
}

.lightbox-container {
  display: flex;
  width: 90%;
  max-width: 1400px;
  height: 90vh;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  position: relative;
}

.lightbox-media {
  flex: 2;
  background: black;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.lightbox-details {
  flex: 1;
  padding: 2rem;
  background: white;
  display: flex;
  flex-direction: column;
}

.details-header {
  margin-bottom: 2rem;
}

.details-header h2 {
  font-family: 'Playfair Display', serif;
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.details-content {
  flex: 1;
  overflow-y: auto;
}

.lightbox-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 1px solid rgba(0,0,0,0.1);
}

.nav-button {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 20px;
  cursor: pointer;
  transition: var(--transition);
}

.nav-button:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-icon {
  font-size: 1.2rem;
}

.lightbox-close {
  position: absolute;
  top: 20px;
  right: 20px;
  background: none;
  border: none;
  color: var(--text-color);
  font-size: 2rem;
  cursor: pointer;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: var(--transition);
}

.lightbox-close:hover {
  background: rgba(0,0,0,0.1);
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes scrollDown {
  0% {
    transform: translateY(-100%);
  }
  100% {
    transform: translateY(100%);
  }
}

/* Responsive */
@media (max-width: 768px) {
  .hero-title {
    font-size: 3rem;
  }
  
  .floating-menu {
    top: auto;
    bottom: 20px;
    right: 50%;
    transform: translateX(50%);
    flex-wrap: wrap;
    justify-content: center;
  }
  
  .magazine-item.wide {
    grid-column: span 1;
  }
  
  .masonry-grid {
    columns: 2 200px;
  }
  
  .lightbox-container {
    flex-direction: column;
  }
  
  .lightbox-media {
    height: 50vh;
  }
  
  .footer-info {
    flex-direction: column;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .masonry-grid {
    columns: 1;
  }
}
</style> 